import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import { HvEmptyState, HvPagination } from "@hitachivantara/uikit-react-core";
import {
  HvTable,
  HvTableBody,
  HvTableCell,
  HvTableContainer,
  HvTableHead,
  HvTableHeader,
  HvTableRow,
  HvTableOptions,
  HvTableInstance,
} from "@hitachivantara/uikit-react-lab";
import { Ban } from "@hitachivantara/uikit-react-icons";
import { IncidentType, Priority, Severity, Status } from "lib/api";
import LoadingContainer from "components/layout/LoadingContainer";
import useStyles from "./styles";

interface IncidentDataRow extends Record<string, unknown> {
  id: string;
  name: string;
  incidentType?: IncidentType;
  priority?: Priority;
  severity?: Severity;
  status?: Status;
  updatedDate: string;
  createdDate: string;
  assignee?: string;
  cameras: number;
  events: number;
}

interface TableProps extends HvTableOptions<IncidentDataRow> {
  instance: HvTableInstance<IncidentDataRow>;
  totalCount?: number;
  disablePagination?: boolean;
  selectAllFiltered?: boolean;
  loading?: boolean;
  labels?: Record<string, string>;
}

export const DEFAULT_PAGE = 0;
export const DEFAULT_PAGE_SIZE = 10;

const Table = ({
  instance,
  totalCount = 0,
  disablePagination = false,
  loading = false,
}: TableProps): JSX.Element => {
  const classes = useStyles();

  const { t } = useTranslation("common");

  const paginationLabels = useMemo(
    () => ({
      pageSizePrev: t("simple_table.pageSizePrefixLabel"),
      pageSizeEntryName: t("simple_table.pageSizeSuffixLabel"),
      pageSizeSelectorDescription: t("simple_table.pageSizeSelectorAriaLabel"),
      pagesSeparator: t("simple_table.pagesSeparator"),
      paginationFirstPageTitle: t("simple_table.firstPageButtonTooltip"),
      paginationPreviousPageTitle: t("simple_table.previousPageButtonTooltip"),
      paginationNextPageTitle: t("simple_table.nextPageButtonTooltip"),
      paginationLastPageTitle: t("simple_table.lastPageButtonTooltip"),
      paginationInputLabel: t("simple_table.currentPageInputAriaLabel"),
      firstPage: t("simple_table.firstPageButtonAriaLabel"),
      previousPage: t("simple_table.previousPageButtonAriaLabel"),
      nextPage: t("simple_table.nextPageButtonAriaLabel"),
      lastPage: t("simple_table.lastPageButtonAriaLabel"),
    }),
    [t]
  );

  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    headers,
    rows,
    state: { pageSize },
    getHvPaginationProps,
  } = instance;

  const EmptyRow = useCallback(
    () => (
      <HvTableRow>
        <HvTableCell
          className={classes.noData}
          // colSpan is missing in the TS definition
          {...{ colSpan: headers.length }}
        >
          <HvEmptyState
            message={t("simple_table.noDataMessage")}
            icon={<Ban role="presentation" />}
          />
        </HvTableCell>
      </HvTableRow>
    ),
    [classes.noData, headers.length, t]
  );
  return (
    <LoadingContainer hidden={!loading}>
      <HvTableContainer className={classes.tableContainer}>
        <HvTable {...getTableProps()} className={classes.table}>
          <HvTableHead className={classes.tableHead}>
            <HvTableRow className={clsx(classes.tableRow)}>
              {headers.map((col) => (
                <HvTableHeader
                  {...col.getHeaderProps()}
                  className={clsx(classes.tableHeader, {
                    [classes.tableCellAction]: col.variant === "actions",
                  })}
                >
                  {col.render("Header")}
                </HvTableHeader>
              ))}
            </HvTableRow>
          </HvTableHead>
          <HvTableBody {...getTableBodyProps()} className={classes.tableBody}>
            {rows.length > 0 ? (
              rows.map((row) => {
                prepareRow(row);

                return (
                  <HvTableRow
                    className={clsx(classes.tableRow)}
                    {...row.getRowProps()}
                  >
                    {row.cells.map((cell) => {
                      const color =
                        cell.row.original?.incidentType?.appearance.color;

                      const cellProps = cell.getCellProps();

                      return (
                        <HvTableCell
                          {...cellProps}
                          className={clsx(classes.tableCell, {
                            [classes.tableCellAction]:
                              cell.column.variant === "actions",
                          })}
                          {...(cell.column.id === "updatedDate" && {
                            style: {
                              ...cellProps.style,
                              borderLeft: `solid 2px ${color}`,
                              paddingLeft: 28,
                            },
                          })}
                        >
                          {cell.render("Cell")}
                        </HvTableCell>
                      );
                    })}
                  </HvTableRow>
                );
              })
            ) : (
              <EmptyRow />
            )}
          </HvTableBody>
        </HvTable>
      </HvTableContainer>
      {!disablePagination &&
        (pageSize !== DEFAULT_PAGE_SIZE || totalCount > pageSize) &&
        rows.length > 0 && (
          <HvPagination {...getHvPaginationProps()} labels={paginationLabels} />
        )}
    </LoadingContainer>
  );
};

export default Table;
